/**
 * StaffSchedule — admin roster: shifts per caregiver.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AdminHeader } from "./_shared";

const STATUS_PALETTE = {
  scheduled: "bg-[#EEF0F4] text-[#6B7280]",
  confirmed: "bg-[#E6EFE8] text-[#1F3A2A]",
  in_progress: "bg-[#FDF1D2] text-[#8C6D1F]",
  completed: "bg-[#E6EFE8] text-[#1F3A2A]",
  cancelled: "bg-[#FCEEEE] text-[#A11D1D]",
  no_show: "bg-[#FCEEEE] text-[#A11D1D]",
};

export default function StaffSchedule() {
  const { t } = useI18n();
  const [shifts, setShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, u, z] = await Promise.all([
        api.get("/admin/ops/shifts").catch(() => ({ data: [] })),
        api.get("/admin/staff").catch(() => ({ data: [] })),
        api.get("/zones").catch(() => ({ data: [] })),
      ]);
      setShifts(s.data || []);
      setStaff((u.data || []).filter((x) => x.role !== "parent"));
      setRooms(z.data || []);
    } catch {
      // ignore
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (form) => {
    try {
      await api.post("/admin/ops/shifts", {
        staff_user_id: form.staff_user_id,
        room_id: form.room_id || null,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: new Date(form.ends_at).toISOString(),
      });
      toast.success("Shift added.");
      setModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ops.errors.generic"));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this shift?")) return;
    try {
      await api.delete(`/admin/ops/shifts/${id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ops.errors.generic"));
    }
  };

  return (
    <main className="p-6 lg:p-10 space-y-6" data-testid="admin-staff-schedule">
      <AdminHeader
        eyebrow={t("ops.staff_schedule.eyebrow")}
        title={t("ops.staff_schedule.title")}
        action={
          <button
            onClick={() => setModal({
              staff_user_id: staff[0]?.id || "",
              room_id: rooms[0]?.id || "",
              starts_at: new Date().toISOString().slice(0, 16),
              ends_at: new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString().slice(0, 16),
            })}
            data-testid="shift-create-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
          >
            <Plus size={14} strokeWidth={2} />
            {t("ops.staff_schedule.create_shift")}
          </button>
        }
      />
      <p className="text-sm text-[#6B7280] max-w-2xl">{t("ops.staff_schedule.blurb")}</p>

      <section className="rounded-3xl bg-white border border-[#EEF0F4] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-[#6B7280] uppercase tracking-[0.1em] text-[10px] bg-[#FAFAFB]">
              <tr>
                <th className="px-5 py-3">{t("ops.staff_schedule.table_caregiver")}</th>
                <th className="px-5 py-3">{t("ops.staff_schedule.table_room")}</th>
                <th className="px-5 py-3">{t("ops.staff_schedule.table_start")}</th>
                <th className="px-5 py-3">{t("ops.staff_schedule.table_end")}</th>
                <th className="px-5 py-3">{t("ops.staff_schedule.table_status")}</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-10 text-center font-serif italic text-[#9CA3AF]">{t("ops.common.loading")}</td></tr>
              ) : shifts.length === 0 ? (
                <tr><td colSpan={6} className="py-10 text-center font-serif italic text-[#9CA3AF]" data-testid="shifts-empty">{t("ops.staff_schedule.no_shifts")}</td></tr>
              ) : (
                shifts.map((s) => (
                  <tr key={s.id} className="border-t border-[#EEF0F4]" data-testid={`shift-row-${s.id}`}>
                    <td className="px-5 py-3 font-medium text-[#1A1B2E]">{s.staff_name}</td>
                    <td className="px-5 py-3 text-[#6B7280]">{s.room_name || "—"}</td>
                    <td className="px-5 py-3 text-[#6B7280] tabular-nums">{s.starts_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-5 py-3 text-[#6B7280] tabular-nums">{s.ends_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-5 py-3">
                      <span className={`text-[10px] tracking-[0.1em] uppercase font-semibold rounded-full px-2 py-0.5 ${STATUS_PALETTE[s.status]}`}>
                        {t(`ops.staff_schedule.status_${s.status}`)}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => del(s.id)} data-testid={`shift-delete-${s.id}`} className="text-[#A11D1D] hover:underline">
                        <Trash2 size={14} strokeWidth={1.6} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {modal && (
        <div role="dialog" aria-modal="true" data-testid="shift-modal" onClick={() => setModal(null)} className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl text-[#1A1B2E]">{t("ops.staff_schedule.create_shift")}</h3>
            <div className="mt-5 space-y-3">
              <label className="block">
                <span className="eyebrow">{t("ops.staff_schedule.staff_label")}</span>
                <select value={modal.staff_user_id} onChange={(e) => setModal({ ...modal, staff_user_id: e.target.value })} data-testid="shift-form-staff" className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white">
                  {staff.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </label>
              <label className="block">
                <span className="eyebrow">{t("ops.staff_schedule.room_label")}</span>
                <select value={modal.room_id} onChange={(e) => setModal({ ...modal, room_id: e.target.value })} data-testid="shift-form-room" className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white">
                  <option value="">—</option>
                  {rooms.map((r) => (<option key={r.id} value={r.id}>{r.name}</option>))}
                </select>
              </label>
              <label className="block">
                <span className="eyebrow">{t("ops.staff_schedule.starts_at")}</span>
                <input type="datetime-local" value={modal.starts_at} onChange={(e) => setModal({ ...modal, starts_at: e.target.value })} data-testid="shift-form-start" className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
              </label>
              <label className="block">
                <span className="eyebrow">{t("ops.staff_schedule.ends_at")}</span>
                <input type="datetime-local" value={modal.ends_at} onChange={(e) => setModal({ ...modal, ends_at: e.target.value })} data-testid="shift-form-end" className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
              </label>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button onClick={() => setModal(null)} className="text-sm text-[#6B7280] hover:text-[#1A1B2E]">{t("ops.common.cancel")}</button>
              <button onClick={() => submit(modal)} data-testid="shift-form-submit" className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]">{t("ops.common.save")}</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
